import { supabase } from '../lib/supabase'
import type {
  SendcloudServiceResponse,
  SendcloudShippingMethodsResponse,
  SendcloudShipmentResponse,
  SendcloudTrackingInfo,
  SendcloudLabelResponse,
  CreateShipmentRequest,
  GetShippingOptionsRequest,
  GetTrackingRequest,
  DownloadLabelRequest,
  SendcloudShipment,
  SendcloudTrackingEvent,
  ShipmentListItem
} from '../types/sendcloud'

class SendcloudService {
  private async callEdgeFunction<T>(action: string, params: Record<string, unknown>): Promise<SendcloudServiceResponse<T>> {
    try {
      const { data, error } = await supabase.functions.invoke('sendcloud-shipment', {
        body: { action, ...params }
      })

      if (error) {
        console.error('Sendcloud edge function error:', error)
        return { success: false, error: error.message }
      }

      return data as SendcloudServiceResponse<T>
    } catch (error) {
      console.error('Sendcloud service error:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      }
    }
  }

  // Get available shipping options for a delivery address
  async getShippingOptions(request: GetShippingOptionsRequest): Promise<SendcloudServiceResponse<SendcloudShippingMethodsResponse>> {
    return this.callEdgeFunction<SendcloudShippingMethodsResponse>('get-shipping-options', {
      country: request.country,
      postalCode: request.postalCode,
      weight: request.weight,
      // Pass through all override parameters
      excludeCarriers: request.excludeCarriers,
      includeOnlyCarriers: request.includeOnlyCarriers,
      maxPrice: request.maxPrice,
      weightRange: request.weightRange,
      serviceTypes: request.serviceTypes,
      filterCriteria: request.filterCriteria,
      overrides: request.overrides,
      customApiParams: request.customApiParams
    })
  }

  // Create a new shipment
  async createShipment(request: CreateShipmentRequest): Promise<SendcloudServiceResponse<SendcloudShipmentResponse>> {
    return this.callEdgeFunction<SendcloudShipmentResponse>('create-shipment', {
      bookingId: request.bookingId,
      shippingMethodId: request.shippingMethodId,
      parcelData: request.parcelData,
      addressOverrides: request.addressOverrides
    })
  }

  // Get tracking information and update database
  async getTracking(request: GetTrackingRequest): Promise<SendcloudServiceResponse<SendcloudTrackingInfo>> {
    return this.callEdgeFunction<SendcloudTrackingInfo>('get-tracking', {
      shipmentId: request.shipmentId
    })
  }

  // Download shipment label
  async downloadLabel(request: DownloadLabelRequest): Promise<SendcloudServiceResponse<SendcloudLabelResponse>> {
    return this.callEdgeFunction<SendcloudLabelResponse>('download-label', {
      shipmentId: request.shipmentId
    })
  }

  // Delete a shipment (and optionally related multi-collo shipments)
  async deleteShipment(shipmentId: number, cancelInSendcloud: boolean = false, relatedShipmentIds: number[] = []): Promise<SendcloudServiceResponse<any>> {
    try {
      let sendcloudResult: any = null
      
      if (cancelInSendcloud) {
        // Try to cancel shipment in Sendcloud first
        try {
          const cancelResult = await this.callEdgeFunction<any>('cancel-shipment', {
            shipmentId: shipmentId.toString()
          })
          
          if (cancelResult.success) {
            console.log('Successfully cancelled shipment in Sendcloud:', cancelResult.data)
            sendcloudResult = cancelResult.data
            
            // If the edge function detected and handled multi-collo shipments,
            // we need to get the list of related shipments that were also cancelled
            if (cancelResult.data?.multi_collo_detected && cancelResult.data?.cancelled_parcels) {
              const edgeFunctionHandledIds = cancelResult.data.cancelled_parcels
                .map((result: any) => result.shipment_id)
                .filter((id: number) => id !== shipmentId) // Exclude the main shipment
              
              // Add these to our list of shipments to delete from database
              const uniqueIds = [...new Set([...relatedShipmentIds, ...edgeFunctionHandledIds])]
              relatedShipmentIds.length = 0
              relatedShipmentIds.push(...uniqueIds)
              console.log(`🔗 Edge function handled multi-collo cancellation for shipments: ${edgeFunctionHandledIds.join(', ')}`)
            }
            
          } else {
            console.warn('Failed to cancel shipment in Sendcloud:', cancelResult.error)
            // Continue with database deletion even if Sendcloud cancellation fails
          }
        } catch (error) {
          console.warn('Error cancelling shipment in Sendcloud:', error)
          // Continue with database deletion even if Sendcloud cancellation fails
        }
      }

      // Delete the shipment record(s) from our database
      const shipmentIdsToDelete = [shipmentId, ...relatedShipmentIds]
      
      const { error: dbError } = await supabase
        .from('sendcloud_shipments')
        .delete()
        .in('id', shipmentIdsToDelete)

      if (dbError) {
        console.error('Error deleting shipments from database:', dbError)
        return { success: false, error: `Failed to delete shipments: ${dbError.message}` }
      }

      console.log(`🗑️ Deleted ${shipmentIdsToDelete.length} shipment(s) from database: ${shipmentIdsToDelete.join(', ')}`)

      return { 
        success: true, 
        data: sendcloudResult
      }
    } catch (error) {
      console.error('Error deleting shipment:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      }
    }
  }

  // Database operations (direct Supabase calls)
  
  // Get shipments for a booking
  async getShipmentsForBooking(bookingId: number): Promise<SendcloudShipment[]> {
    try {
      const { data, error } = await supabase
        .from('sendcloud_shipments')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching shipments:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching shipments:', error)
      return []
    }
  }

  // Get shipment by ID with tracking events
  async getShipmentById(shipmentId: number): Promise<ShipmentListItem | null> {
    try {
      const { data, error } = await supabase
        .from('sendcloud_shipments')
        .select(`
          *,
          booking:bookings(
            id,
            booking_reference,
            company:companies(name)
          )
        `)
        .eq('id', shipmentId)
        .single()

      if (error) {
        console.error('Error fetching shipment:', error)
        return null
      }

      // Get latest tracking event
      const { data: trackingData } = await supabase
        .from('sendcloud_tracking_events')
        .select('*')
        .eq('shipment_id', shipmentId)
        .order('event_timestamp', { ascending: false })
        .limit(1)

      return {
        ...data,
        latest_tracking_event: trackingData?.[0] || undefined
      }
    } catch (error) {
      console.error('Error fetching shipment:', error)
      return null
    }
  }

  // Get tracking events for a shipment
  async getTrackingEvents(shipmentId: number): Promise<SendcloudTrackingEvent[]> {
    try {
      const { data, error } = await supabase
        .from('sendcloud_tracking_events')
        .select('*')
        .eq('shipment_id', shipmentId)
        .order('event_timestamp', { ascending: false })

      if (error) {
        console.error('Error fetching tracking events:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching tracking events:', error)
      return []
    }
  }

  // Get all shipments for admin view (with pagination)
  async getAllShipments(
    page: number = 1, 
    limit: number = 20,
    filters?: { status?: string; carrier?: string }
  ): Promise<{ shipments: ShipmentListItem[]; total: number }> {
    try {
      let query = supabase
        .from('sendcloud_shipments')
        .select(`
          *,
          booking:bookings(
            id,
            booking_reference,
            company:companies(name)
          )
        `, { count: 'exact' })

      // Apply filters
      if (filters?.status) {
        query = query.eq('status', filters.status)
      }
      if (filters?.carrier) {
        query = query.eq('carrier_name', filters.carrier)
      }

      // Apply pagination
      const from = (page - 1) * limit
      const to = from + limit - 1

      const { data, error, count } = await query
        .range(from, to)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching all shipments:', error)
        return { shipments: [], total: 0 }
      }

      return {
        shipments: data || [],
        total: count || 0
      }
    } catch (error) {
      console.error('Error fetching all shipments:', error)
      return { shipments: [], total: 0 }
    }
  }

  // Utility function to check if booking has delivery address
  async hasDeliveryAddress(bookingId: number): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('booking_delivery')
        .select('id')
        .eq('booking_id', bookingId)
        .single()

      return !error && !!data
    } catch {
      return false
    }
  }

  // Get booking delivery address
  async getBookingDeliveryAddress(bookingId: number) {
    try {
      const { data, error } = await supabase
        .from('booking_delivery')
        .select('*')
        .eq('booking_id', bookingId)
        .single()

      if (error) {
        console.error('Error fetching delivery address:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error fetching delivery address:', error)
      return null
    }
  }
}

// Export singleton instance
export const sendcloudService = new SendcloudService()
export default sendcloudService 